import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import PageLoader from "@/components/loader/PageLoader";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUser } from "@/contexts/UserContext";
import { formatPhone } from "@/utils/helper";
import { Employee, EmployeeAvailability } from "@/utils/types";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import _ from "lodash";
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Check, Plus, X } from "lucide-react";
import EmployeeAvailabilityInput, { EmployeeAvailabilityInputRef } from "@/components/selectors/EmployeeAvailabilityInput";

interface Service {
  id: number;
  name: string;
  description: string;
  product_type: string;
}

const formSchema = z.object({
  first_name: z.string().nonempty("First Name is required"),
  last_name: z.string().nonempty("Last Name is required"),
  phone: z.string().regex(/^\d{10}$/, {
    message: "Invalid phone number, must be 10 digits",
  }),
  email: z.string().email(),
  job_title: z.enum(["SERVER", "MANAGER", "OWNER"], {
    message: "Invalid job title, must be Server or Manager",
  }),
  employee_pin: z
    .string()
    .length(4, "Employee ID must be exactly 4 characters"),
  branch: z.number(),
  service_provided: z.array(z.number()),
  availability_hours: z.array(
    z.object({
      day: z.string(),
      start_time: z.string(),
      end_time: z.string(),
      id: z.number().optional(),
    }),
  ).default([]),
});

const EditEmployee = () => {
  const { user } = useUser();
  const { id: employeeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [services, setServices] = useState<Service[]>([]);
  const [availabilityHours, setAvailabilityHours] = useState<
    EmployeeAvailability[]
  >([]);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      job_title: "SERVER",
      branch: user?.selectedBranch?.id || 0,
      employee_pin: "",
      service_provided: [],
      availability_hours: [],
    },
    shouldUnregister: false,
  });
  const [originalEmployee, setOriginalEmployee] = useState<Employee>({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    job_title: "SERVER",
    branch: user?.selectedBranch?.id || 0,
    employee_pin: "",
    service_provided: [],
    availability_hours: [],
  });

  // Create a ref to access the EmployeeAvailabilityInput component's methods
  const availabilityInputRef = useRef<EmployeeAvailabilityInputRef>(null);

  const fetchEmployee = async () => {
    if (loading || !axiosInstance || !employeeId) return;
    try {
      const employeeRes = await axiosInstance.get(
        `/api/v1/employee/${employeeId}/`,
      );

      if (employeeRes.data) {
        if (employeeRes.data.job_title === "") {
          employeeRes.data.job_title = "SERVER";
        }
        const data = {
          ...employeeRes.data,
          phone: employeeRes.data.phone.substring(2),
          service_provided: employeeRes.data.service_provided || [],
        };
        form.reset(data);
        const availability = data.availability || [];
        setAvailabilityHours(availability);
        setOriginalEmployee({
          ...data,
          availability_hours: availability,
        });
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      toast.error("Failed to fetch employee data");
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
    }
  }, [axiosInstance, employeeId, loading]);

  useEffect(() => {
    const fetchServices = async () => {
      if (!axiosInstance || !user?.selectedBranch?.id) return;

      try {
        const response = await axiosInstance.get(
          `/api/v1/product/?branch=${user.selectedBranch.id}&product_type=Service&is_active=true&page_size=100`,
        );
        if (response.data.results) {
          setServices(
            response.data.results.filter(
              (service: Service) => service.product_type === "Service",
            ),
          );
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        toast.error("Failed to fetch available services");
      }
    };

    fetchServices();
  }, [axiosInstance, user?.selectedBranch?.id]);

  // Create a debounced effect to check and set dirty state
  const debouncedCheckDirtyState = _.debounce(() => {
    if (!originalEmployee) return;

    const formValues = form.getValues();
    const normalizedFormValues = {
      ...formValues,
      phone: formValues.phone.replace(/\D/g, ""),
      service_provided: formValues.service_provided || [],
    };

    const normalizedOriginalEmployee = {
      ...originalEmployee,
      phone: originalEmployee.phone?.replace(/\D/g, ""),
      service_provided: originalEmployee.service_provided || [],
      availability_hours: originalEmployee.availability_hours || [],
    };

    const isFormChanged = !_.isEqual(
      _.omit(normalizedFormValues, ["availability_hours"]),
      _.omit(normalizedOriginalEmployee, ["availability_hours"]),
    );

    // Use simple length comparison first as a quick check
    const isAvailabilityChanged = 
      (normalizedOriginalEmployee.availability_hours.length !== availabilityHours.length);
    
    // Only do deep comparison if lengths are the same
    if (!isAvailabilityChanged && availabilityHours.length > 0) {
      // Sort both arrays by day and start time for consistent comparison
      const sortedOriginal = _.sortBy(
        normalizedOriginalEmployee.availability_hours, 
        ['day', 'start_time']
      );
      
      const sortedCurrent = _.sortBy(
        availabilityHours, 
        ['day', 'start_time']
      );
      
      // Compare only the fields that matter for business logic
      const simplifiedOriginal = sortedOriginal.map(({ day, start_time, end_time }) => 
        ({ day, start_time, end_time })
      );
      
      const simplifiedCurrent = sortedCurrent.map(({ day, start_time, end_time }) => 
        ({ day, start_time, end_time })
      );
      
      // Now do the deep comparison on the simplified objects
      const deepAvailabilityChanged = !_.isEqual(simplifiedCurrent, simplifiedOriginal);
      
      console.log("EditEmployee: Setting isDirty state:", isFormChanged || deepAvailabilityChanged);
      setIsDirty(isFormChanged || deepAvailabilityChanged);
    } else {
      console.log("EditEmployee: Setting isDirty state:", isFormChanged || isAvailabilityChanged);
      setIsDirty(isFormChanged || isAvailabilityChanged);
    }
  }, 300);

  useEffect(() => {
    if (originalEmployee) {
      debouncedCheckDirtyState();

      // Clean up on unmount
      return () => {
        debouncedCheckDirtyState.cancel();
      };
    }
  }, [form.watch(), originalEmployee, availabilityHours]);

  useEffect(() => {
    form.setValue('availability_hours', availabilityHours);
  }, [availabilityHours, form]);

  const handleDiscard = () => {
    if (originalEmployee) {
      form.reset(originalEmployee);
      setAvailabilityHours(originalEmployee.availability_hours || []);
      setIsDirty(false);
    }
  };

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (loading || !axiosInstance) return;
    setIsSaving(true);

    try {
      const body = {
        ...data,
        phone: `+1${data.phone.replace(/\D/g, "")}`,
        availability_hours: availabilityHours,
      };
      const res = await axiosInstance.patch(
        `/api/v1/employee/${employeeId}/`,
        body,
      );
      toast.success(
        `Employee updated successfully with passkey: ${res.data.employee_pin}`,
      );
      navigate("/employee");
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const errorObj = error?.response?.data;
        const errorKey = Object.keys(errorObj)[0];
        const errorData = errorObj[errorKey as keyof typeof errorObj];
        toast.error(`Error in updating employee: ${errorData}`);
      } else {
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  // A separate function for the ContextualSaveBar that doesn't navigate away
  const saveChanges = async () => {
    console.log("EditEmployee: saveChanges function called");
    if (loading || !axiosInstance || !employeeId) {
      console.error("EditEmployee: Cannot save - missing axiosInstance or employeeId");
      return;
    }
    
    // Prevent multiple simultaneous saves
    if (isSaving) {
      console.log("EditEmployee: Already saving, ignoring duplicate save request");
      return;
    }
    
    setIsSaving(true);
    console.log("EditEmployee: Setting isSaving to true");

    try {
      // First, ensure all availability hours are saved through the ref if there are any changes
      let savedHoursData: EmployeeAvailability[] = [];
      
      // Check if we have a valid reference and if availability hours are dirty
      // and need to be saved
      const isAvailabilityDirty = !_.isEqual(
        _.sortBy(originalEmployee.availability_hours || [], ['day', 'start_time']),
        _.sortBy(availabilityHours, ['day', 'start_time'])
      );
      
      if (availabilityInputRef.current && isAvailabilityDirty) {
        console.log("EditEmployee: Saving availability hours first");
        try {
          // This will save any pending availability hours
          savedHoursData = await availabilityInputRef.current.saveAllAvailability();
          console.log("EditEmployee: Availability hours saved successfully:", savedHoursData);
        } catch (error) {
          console.error("EditEmployee: Failed to save availability hours:", error);
          toast.error("Failed to save availability hours");
          // Continue with the employee save even if hours fail
        }
      } else {
        console.log("EditEmployee: No availability hours changes to save");
        savedHoursData = availabilityHours;
      }
      
      // Get form values
      const formValues = form.getValues();
      
      // Make sure the data is valid
      const validationResult = formSchema.safeParse(formValues);
      if (!validationResult.success) {
        console.error("EditEmployee: Form validation failed:", validationResult.error);
        toast.error("Please fix the form errors before saving");
        setIsSaving(false);
        return;
      }
      
      const body = {
        ...formValues,
        phone: `+1${formValues.phone.replace(/\D/g, "")}`,
      };
      
      // Log the request for debugging
      console.log("EditEmployee: Saving employee with data:", body);
      
      console.log("EditEmployee: Making API call to patch employee");
      const res = await axiosInstance.patch(
        `/api/v1/employee/${employeeId}/`,
        body,
      );
      
      console.log("EditEmployee: API call successful, response:", res.data);
      
      // Update both local state and form data with the saved values
      const updatedEmployee = {
        ...formValues,
        phone: formValues.phone,
        service_provided: formValues.service_provided || [],
        // Use the saved hours from our local operation - they are more up-to-date than 
        // what might come back in the response
        availability_hours: savedHoursData.length > 0 ? savedHoursData : (res.data.availability || []),
      };
      
      console.log("EditEmployee: Updating originalEmployee state");
      setOriginalEmployee(updatedEmployee);
      
      // Update availability hours to match what we've saved
      if (savedHoursData.length > 0) {
        console.log("EditEmployee: Updating availabilityHours state with saved data");
        setAvailabilityHours(savedHoursData);
      }
      
      // Set isDirty to false - this is critical to make contextual save bar disappear
      console.log("EditEmployee: Setting isDirty to false");
      setIsDirty(false);
      
      toast.success(
        `Employee updated successfully with passkey: ${res.data.employee_pin}`,
      );
      
      console.log("EditEmployee: Save completed successfully");
      return res.data;
      
    } catch (error: any) {
      console.error("EditEmployee: Error saving employee:", error);
      if (axios.isAxiosError(error)) {
        const errorObj = error?.response?.data;
        const errorKey = Object.keys(errorObj || {})[0];
        const errorData = errorObj ? errorObj[errorKey as keyof typeof errorObj] : null;
        toast.error(`Error in updating employee: ${errorData || error.message}`);
      } else {
        toast.error(`An unexpected error occurred: ${error.message || "Unknown error"}`);
      }
      throw error; // Rethrow the error so the calling function knows there was a problem
    } finally {
      console.log("EditEmployee: Setting isSaving to false");
      setIsSaving(false);
    }
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        <h1 className="mb-4 text-xl font-bold">Update Employee</h1>
        <div className="flex flex-col space-y-6">
          <div className="rounded-lg bg-white p-4 shadow">
            <h1 className="font-semibold lg:text-lg">Employee Information</h1>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col space-y-5"
              >
                <div className="w-full space-y-2">
                  <Label htmlFor="name">
                    Employee Name
                    <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid w-full grid-cols-2 space-x-4">
                    <FormField
                      control={form.control}
                      name="first_name"
                      render={({ field }) => (
                        <FormItem>
                          <Input {...field} placeholder="First Name" required />
                          <FormMessage>
                            {form.formState.errors.first_name?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="last_name"
                      render={({ field }) => (
                        <FormItem>
                          <Input {...field} placeholder="Last Name" required />
                          <FormMessage>
                            {form.formState.errors.last_name?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="grid-col-1 grid w-full lg:grid-cols-2 lg:space-x-4">
                  <div className="space-y-2">
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="phone">
                            Phone
                            <span className="text-red-500">*</span>
                          </FormLabel>
                          <Input
                            {...field}
                            placeholder="Phone"
                            required
                            value={formatPhone(field.value)}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, "");
                              field.onChange(value);
                            }}
                          />
                          <FormMessage>
                            {form.formState.errors.phone?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="email">
                            Email
                            <span className="text-red-500">*</span>
                          </FormLabel>
                          <Input {...field} placeholder="Email" required />
                          <FormMessage>
                            {form.formState.errors.email?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="grid-col-1 grid w-full lg:grid-cols-2 lg:space-x-4">
                  <div className="space-y-2">
                    <FormField
                      control={form.control}
                      name="job_title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="job_title">
                            Job Title
                            <span className="text-red-500">*</span>
                          </FormLabel>
                          <select
                            {...field}
                            className="form-select mt-1 block h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                          >
                            <option value="SERVER">Server</option>
                            <option value="MANAGER">Manager</option>
                          </select>
                          <FormMessage>
                            {form.formState.errors.job_title?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div>
                    <FormField
                      control={form.control}
                      name="employee_pin"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel htmlFor="employee_pin">
                            Employee Pin
                            <span className="text-red-500">*</span>
                          </FormLabel>
                          <Input
                            {...field}
                            placeholder="Employee Pin"
                            required
                            type="number"
                            maxLength={4}
                            minLength={4}
                          />
                          <FormMessage>
                            {form.formState.errors.employee_pin?.message}
                          </FormMessage>
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
                <div className="rounded-lg bg-white p-4 shadow">
                  <FormField
                    control={form.control}
                    name="service_provided"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-lg font-semibold">
                          Services Provided
                          {field.value.length === 0 && (
                            <span className="text-red-500">*</span>
                          )}
                        </FormLabel>
                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                          {services.map((service) => (
                            <div
                              key={service.id}
                              className={`cursor-pointer rounded-lg border p-4 transition-all hover:border-red-500 ${
                                field.value.includes(service.id)
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-200"
                              }`}
                              onClick={() => {
                                const newValue = field.value.includes(
                                  service.id,
                                )
                                  ? field.value.filter(
                                      (id) => id !== service.id,
                                    )
                                  : [...field.value, service.id];
                                field.onChange(newValue);
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <h3 className="font-medium">
                                    {service.name}
                                  </h3>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {service.description}
                                  </p>
                                </div>
                                <div
                                  className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                                    field.value.includes(service.id)
                                      ? "border-red-500 bg-red-500 text-white"
                                      : "border-gray-300"
                                  }`}
                                >
                                  {field.value.includes(service.id) && (
                                    <Check className="h-3 w-3" />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {services.length === 0 && (
                          <p className="text-center text-gray-500">
                            No services available for this branch
                          </p>
                        )}
                        <FormMessage>
                          {form.formState.errors.service_provided?.message}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end space-x-4">
                  <Button
                    onClick={() => navigate("/employee")}
                    variant="secondary"
                    className="bg-gray-500 text-white hover:text-gray-600"
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    className="bg-red-500 text-white hover:bg-red-600"
                    type="button"
                    onClick={saveChanges}
                  >
                    Update Employee
                  </Button>
                </div>
              </form>
            </Form>
          </div>

          {/* Employee Availability Hours Section */}
          <div className="rounded-lg bg-white p-4 shadow">
            <EmployeeAvailabilityInput
              ref={availabilityInputRef}
              availabilityHours={availabilityHours}
              setAvailabilityHours={setAvailabilityHours}
              employeeId={parseInt(employeeId || "0")}
              onSuccessfulSave={(hours) => {
                console.log("EditEmployee: onSuccessfulSave called with hours:", hours);
                // When hours are successfully saved via the component's save functionality,
                // update the original employee data to match to prevent the contextual save bar from showing
                if (originalEmployee && hours.length > 0) {
                  console.log("EditEmployee: Updating originalEmployee with new hours");
                  // Create a completely new object to ensure React detects the change
                  const updatedOriginalEmployee = {
                    ...originalEmployee,
                    availability_hours: [...hours],
                  };
                  setOriginalEmployee(updatedOriginalEmployee);
                  
                  // Also update the form's value for availability_hours
                  console.log("EditEmployee: Updating form with new hours");
                  form.setValue('availability_hours', hours);
                  
                  // We need to manually mark the form as pristine
                  // after individual hour saves
                  setTimeout(() => {
                    console.log("EditEmployee: Forcing isDirty to false after availability save");
                    setIsDirty(false);
                  }, 0);
                }
              }}
            />
          </div>
        </div>
      </div>
      <ContextualSaveBar
        isDirty={isDirty}
        isSaving={isSaving}
        onDiscard={handleDiscard}
        onSave={saveChanges}
      />
    </PageLoader>
  );
};

export default EditEmployee;
